import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { User } from '../../models/identity/user.model';
import { ConversationView } from '../type/conversation-view.type';
import { Conversation } from '../type/conversation.type';
import { Message } from '../type/message.type';

export interface ChatState {
    conversations: ConversationView[];
    activeConversationId: string | null;
    messages: Map<string, Message[]>;
    isLoading: boolean;
    isModalOpen: boolean;
    targetUser: User | null;
}

const initialState: ChatState = {
    conversations: [],
    activeConversationId: null,
    messages: new Map<string, Message[]>(),
    isLoading: false,
    isModalOpen: false,
    targetUser: null,
};

@Injectable({
    providedIn: 'root',
})
export class ChatStateService {
    private readonly state$ = new BehaviorSubject<ChatState>(initialState);

    // Selectors - Observables publics
    readonly conversations$: Observable<ConversationView[]> = this.state$.pipe(map((state) => state.conversations));

    readonly activeConversationId$: Observable<string | null> = this.state$.pipe(map((state) => state.activeConversationId));

    readonly activeConversation$: Observable<Conversation | null> = combineLatest([this.conversations$, this.activeConversationId$]).pipe(
        map(([conversations, activeId]) => {
            if (!activeId) return null;
            const convView = conversations.find((cv) => cv.conversation.id === activeId);
            return convView ? convView.conversation : null;
        }),
    );

    readonly activeConversationView$: Observable<ConversationView | null> = combineLatest([this.conversations$, this.activeConversationId$]).pipe(
        map(([conversations, activeId]) => {
            if (!activeId) return null;
            return conversations.find((cv) => cv.conversation.id === activeId) || null;
        }),
    );

    readonly activeMessages$: Observable<Message[]> = combineLatest([this.state$, this.activeConversationId$]).pipe(
        map(([state, activeId]) => {
            if (!activeId) return [];
            return state.messages.get(activeId) || [];
        }),
    );

    readonly isLoading$: Observable<boolean> = this.state$.pipe(map((state) => state.isLoading));

    readonly isModalOpen$: Observable<boolean> = this.state$.pipe(map((state) => state.isModalOpen));

    readonly targetUser$: Observable<User | null> = this.state$.pipe(map((state) => state.targetUser));

    readonly hasUnreadMessages$: Observable<boolean> = this.conversations$.pipe(
        map((conversations) => conversations.some((cv) => (cv.conversation.unreadCount || 0) > 0)),
    );

    // Actions - Méthodes de mutation
    setConversations(conversations: ConversationView[]): void {
        this.updateState({ conversations });
    }

    addConversation(conversationView: ConversationView): void {
        const currentState = this.state$.value;
        const exists = currentState.conversations.some((cv) => cv.conversation.id === conversationView.conversation.id);

        if (!exists) {
            this.updateState({
                conversations: [conversationView, ...currentState.conversations],
            });
        }
    }

    updateConversation(conversationId: string, updates: Partial<Conversation>): void {
        const currentState = this.state$.value;
        const updatedConversations = currentState.conversations.map((cv) => {
            if (cv.conversation.id === conversationId) {
                return {
                    ...cv,
                    conversation: { ...cv.conversation, ...updates },
                };
            }
            return cv;
        });
        this.updateState({ conversations: updatedConversations });
    }

    incrementUnreadCount(conversationId: string): void {
        const currentState = this.state$.value;
        const updatedConversations = currentState.conversations.map((cv) => {
            if (cv.conversation.id === conversationId) {
                return {
                    ...cv,
                    conversation: {
                        ...cv.conversation,
                        unreadCount: (cv.conversation.unreadCount || 0) + 1,
                    },
                };
            }
            return cv;
        });
        this.updateState({ conversations: updatedConversations });
    }

    resetUnreadCount(conversationId: string): void {
        this.updateConversation(conversationId, { unreadCount: 0 });
    }

    setActiveConversation(conversationId: string | null): void {
        this.updateState({ activeConversationId: conversationId });
    }

    setMessages(conversationId: string, messages: Message[]): void {
        const currentState = this.state$.value;
        const newMessagesMap = new Map(currentState.messages);
        newMessagesMap.set(conversationId, messages);
        this.updateState({ messages: newMessagesMap });
    }

    addMessage(conversationId: string, message: Message): void {
        const currentState = this.state$.value;
        const existingMessages = currentState.messages.get(conversationId) || [];

        // Éviter les doublons (vérifier par ID)
        const messageExists = existingMessages.some((m) => m.id === message.id);
        if (messageExists) return;

        const newMessagesMap = new Map(currentState.messages);
        newMessagesMap.set(conversationId, [message, ...existingMessages]);
        this.updateState({ messages: newMessagesMap });
    }

    updateLastMessage(conversationId: string, message: Message): void {
        this.updateConversation(conversationId, {
            lastMessagePreview: message.content,
            lastMessageAt: message.sentAt,
        });
    }

    setLoading(isLoading: boolean): void {
        this.updateState({ isLoading });
    }

    setModalOpen(isModalOpen: boolean): void {
        this.updateState({ isModalOpen });
    }

    setTargetUser(targetUser: User | null): void {
        this.updateState({ targetUser });
    }

    reset(): void {
        this.state$.next(initialState);
    }

    // Méthode utilitaire privée pour mise à jour immutable
    private updateState(partialState: Partial<ChatState>): void {
        this.state$.next({
            ...this.state$.value,
            ...partialState,
        });
    }
}
